import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { leadsApi } from '@/services/api';
import {
  leadDetailActivities,
  leadDocuments,
  leadCommunications,
  leadPipelineHistory,
  type LeadDetailActivity,
  type LeadDocument,
  type CommunicationLog,
  type PipelineStageEvent,
} from '@/mocks/dashboard';

type DetailTab = 'overview' | 'activity' | 'pipeline' | 'documents' | 'communications';

interface DetailLead {
  id: number;
  name: string;
  phone: string;
  email: string;
  source: string;
  propertyInterest: string;
  budgetRange: string;
  timeline: string;
  customerType: string;
  notes: string;
  status: string;
  assignedRM: string;
  score: number;
  value: number;
  daysInStage: number;
  createdAt: string;
}

function mapApiLead(apiLead: Record<string, unknown>): DetailLead {
  const stage = String(apiLead.stage || apiLead.status || 'new');
  const stageMap: Record<string, string> = {
    new: 'New Lead',
    contacted: 'Contacted',
    qualified: 'Qualified',
    site_visit: 'Site Visit',
    negotiation: 'Negotiation',
    converted: 'Converted',
    lost: 'Lost',
  };
  return {
    id: Number(apiLead.id),
    name: String(apiLead.name || ''),
    phone: String(apiLead.phone || ''),
    email: String(apiLead.email || ''),
    source: String(apiLead.source || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
    propertyInterest: String(apiLead.property_interest || apiLead.listing_title || '-'),
    budgetRange: String(apiLead.budget || '-'),
    timeline: '-',
    customerType: String(apiLead.priority || '-'),
    notes: String(apiLead.notes || '-'),
    status: stageMap[stage] || stage.charAt(0).toUpperCase() + stage.slice(1).replace(/_/g, ' '),
    assignedRM: String(apiLead.assigned_rm || '-'),
    score: Number(apiLead.score || 0),
    value: Number(apiLead.deal_value || 0),
    daysInStage: 0,
    createdAt: String(apiLead.created_at || ''),
  };
}

const pipelineStages = ['New Lead', 'Contacted', 'Qualified', 'Negotiation', 'Converted', 'Closed Won', 'Closed Lost'];
const stageColors: Record<string, string> = {
  'New Lead': 'bg-blue-500/15 text-blue-400 border-blue-500/25',
  'Contacted': 'bg-accent-100 text-accent-700 border-accent-200',
  'Qualified': 'bg-teal-500/15 text-teal-400 border-teal-500/25',
  'Negotiation': 'bg-secondary-100 text-secondary-700 border-secondary-200',
  'Converted': 'bg-primary-100 text-primary-700 border-primary-200',
  'Closed Won': 'bg-green-500/15 text-green-400 border-green-500/25',
  'Closed Lost': 'bg-rose-500/15 text-rose-400 border-rose-500/25',
};

const activityIcons: Record<string, string> = {
  call: 'ri-phone-line',
  email: 'ri-mail-line',
  meeting: 'ri-calendar-check-line',
  note: 'ri-sticky-note-line',
  document: 'ri-file-text-line',
  status_change: 'ri-arrow-left-right-line',
  score_change: 'ri-bar-chart-2-line',
  assignment: 'ri-user-shared-line',
  site_visit: 'ri-map-pin-line',
  negotiation: 'ri-exchange-funds-line',
  payment: 'ri-bank-card-line',
};

const activityColors: Record<string, string> = {
  call: 'bg-accent-100 text-accent-700',
  email: 'bg-primary-100 text-primary-700',
  meeting: 'bg-teal-500/15 text-teal-400',
  note: 'bg-secondary-100 text-secondary-700',
  document: 'bg-foreground-200/60 text-foreground-600',
  status_change: 'bg-green-500/15 text-green-400',
  score_change: 'bg-accent-100 text-accent-700',
  assignment: 'bg-foreground-200/60 text-foreground-600',
  site_visit: 'bg-blue-500/15 text-blue-400',
  negotiation: 'bg-secondary-100 text-secondary-700',
  payment: 'bg-green-500/15 text-green-400',
};

const docTypeLabels: Record<string, string> = {
  agreement: 'Agreement',
  nda: 'NDA',
  proposal: 'Proposal',
  kyc: 'KYC',
  invoice: 'Invoice',
  report: 'Report',
  other: 'Other',
};

const docTypeIcons: Record<string, string> = {
  agreement: 'ri-file-user-line',
  nda: 'ri-shield-check-line',
  proposal: 'ri-file-paper-2-line',
  kyc: 'ri-id-card-line',
  invoice: 'ri-bill-line',
  report: 'ri-file-chart-line',
  other: 'ri-file-line',
};

const channelIcons: Record<string, string> = {
  call: 'ri-phone-line',
  email: 'ri-mail-line',
  whatsapp: 'ri-whatsapp-line',
  sms: 'ri-chat-1-line',
  meeting: 'ri-calendar-check-line',
};

const scoreColor = (score: number) => {
  if (score >= 75) return 'text-teal-400';
  if (score >= 50) return 'text-accent-700';
  if (score >= 30) return 'text-secondary-700';
  return 'text-rose-400';
};

function formatDate(d: string) {
  if (!d) return '-';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(d: string) {
  if (!d) return '-';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) + ' · ' + dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
}

function formatRelativeTime(d: string) {
  const diff = Date.now() - new Date(d).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(d);
}

function formatINR(val: number): string {
  if (val >= 10000000) return '\u20B9' + (val / 10000000).toFixed(1) + 'Cr';
  if (val >= 100000) return '\u20B9' + (val / 100000).toFixed(1) + 'L';
  if (val >= 1000) return '\u20B9' + (val / 1000).toFixed(0) + 'K';
  return '\u20B9' + val.toFixed(0);
}

export default function LeadDetailPage() {
  const { leadId } = useParams<{ leadId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<DetailTab>('overview');
  const [actionMsg, setActionMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activityFilter, setActivityFilter] = useState<string>('');
  const [newNote, setNewNote] = useState('');
  const [lead, setLead] = useState<DetailLead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const id = parseInt(leadId || '0', 10);

  useEffect(() => {
    if (!id) {
      setError('Invalid lead ID');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    leadsApi.get(id)
      .then((resp) => {
        const apiLead = (resp as unknown as { data: Record<string, unknown> })?.data;
        if (!apiLead) {
          setError('Lead not found');
        } else {
          setLead(mapApiLead(apiLead));
        }
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load lead');
      })
      .finally(() => setLoading(false));
  }, [id]);

  const activities = leadDetailActivities[id] || [];
  const documents = leadDocuments[id] || [];
  const communications = leadCommunications[id] || [];
  const pipelineHistory = leadPipelineHistory[id] || [];

  const filteredActivities = useMemo(() => {
    if (!activityFilter) return activities;
    return activities.filter(a => a.type === activityFilter);
  }, [activities, activityFilter]);

  const activityTypes = useMemo(() => {
    const types = new Set(activities.map(a => a.type));
    return Array.from(types);
  }, [activities]);

  const clearMsg = () => setTimeout(() => setActionMsg(null), 3000);

  const handleAddNote = () => {
    if (!newNote.trim()) return;
    setActionMsg({ type: 'success', text: 'Note added to activity timeline.' });
    setNewNote('');
    clearMsg();
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-12 h-12 rounded-full bg-background-200/60 animate-pulse mb-4" />
          <p className="text-sm text-foreground-500">Loading lead details...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !lead) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-full bg-rose-500/15 flex items-center justify-center mb-4">
            <i className="ri-user-unfollow-line text-2xl text-rose-400" />
          </div>
          <h2 className="text-lg font-bold text-foreground-950">Lead Not Found</h2>
          <p className="text-sm text-foreground-500 mt-1">{error || "The lead you're looking for doesn't exist or has been removed."}</p>
          <button onClick={() => navigate('/dashboard/leads')} className="mt-4 px-5 py-2.5 rounded-xl bg-accent-500 text-white text-sm font-semibold cursor-pointer hover:bg-accent-400 whitespace-nowrap">
            <i className="ri-arrow-left-line mr-1.5" /> Back to Leads
          </button>
        </div>
      </DashboardLayout>
    );
  }

  const tabs: { key: DetailTab; label: string; icon: string; count?: number }[] = [
    { key: 'overview', label: 'Overview', icon: 'ri-dashboard-line' },
    { key: 'activity', label: 'Activity', icon: 'ri-history-line', count: activities.length },
    { key: 'pipeline', label: 'Pipeline', icon: 'ri-git-branch-line', count: pipelineHistory.length },
    { key: 'documents', label: 'Documents', icon: 'ri-folder-line', count: documents.length },
    { key: 'communications', label: 'Communications', icon: 'ri-chat-3-line', count: communications.length },
  ];

  const currentStageIndex = pipelineStages.findIndex(s => lead.status === s || lead.status.toLowerCase().replace(/\s+/g, '_') === s.toLowerCase().replace(/\s+/g, '_'));

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Toast */}
        {actionMsg && (
          <div className={`fixed top-4 right-4 z-[1100] px-4 py-3 rounded-xl text-sm font-semibold shadow-lg ${actionMsg.type === 'success' ? 'bg-teal-500/20 text-teal-400 border border-teal-500/30' : 'bg-rose-500/20 text-rose-400 border border-rose-500/30'}`}>
            {actionMsg.text}
          </div>
        )}

        {/* Header */}
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/dashboard/leads')} className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-background-200/60 text-foreground-500 hover:text-foreground-950 cursor-pointer">
            <i className="ri-arrow-left-line" />
          </button>
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-accent-500 to-primary-500 flex items-center justify-center flex-shrink-0">
            <span className="text-sm font-bold text-foreground-950">{lead.name.split(' ').map(n => n[0]).join('')}</span>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-xl font-bold text-foreground-950">{lead.name}</h1>
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${
                lead.status === 'Closed Won' ? 'bg-green-500/15 text-green-400 border-green-500/25' :
                lead.status === 'Closed Lost' ? 'bg-rose-500/15 text-rose-400 border-rose-500/25' :
                'bg-accent-100 text-accent-700 border-accent-200'
              }`}>{lead.status}</span>
            </div>
            <p className="text-xs text-foreground-500">{lead.email} · {lead.phone} · <span className={`font-semibold ${scoreColor(lead.score || 0)}`}>Score: {lead.score}</span></p>
          </div>
          <div className="flex items-center gap-2">
            <button className="px-3 py-2 rounded-xl bg-white border border-background-200/60 text-xs text-foreground-500 hover:text-foreground-950 cursor-pointer whitespace-nowrap">
              <i className="ri-more-2-line" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 bg-background-50 rounded-xl p-1 w-fit flex-wrap">
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm whitespace-nowrap cursor-pointer transition-all ${activeTab === tab.key ? 'bg-accent-100 text-accent-700 font-semibold' : 'text-foreground-500 hover:text-foreground-950'}`}>
              <i className={tab.icon} /> {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${activeTab === tab.key ? 'bg-accent-700 text-white' : 'bg-background-200/60 text-foreground-500'}`}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* ── OVERVIEW TAB ── */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Lead Info */}
            <div className="lg:col-span-2 space-y-4">
              <div className="rounded-xl border border-background-200/60 bg-white p-6">
                <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-4">Lead Information</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Source</span><span className="text-sm text-foreground-950 flex items-center gap-1.5 mt-0.5"><i className={`${lead.source === 'Website' ? 'ri-global-line' : lead.source === 'WhatsApp' ? 'ri-whatsapp-line' : lead.source === 'Referral' ? 'ri-user-heart-line' : lead.source === 'Google Ads' ? 'ri-google-line' : 'ri-mail-line'} text-accent-700 text-xs`} />{lead.source}</span></div>
                  <div><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Customer Type</span><span className="text-sm text-foreground-950 mt-0.5 block">{lead.customerType}</span></div>
                  <div><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Budget Range</span><span className="text-sm text-foreground-950 mt-0.5 block">{lead.budgetRange}</span></div>
                  <div><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Timeline</span><span className="text-sm text-foreground-950 mt-0.5 block">{lead.timeline}</span></div>
                  <div><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Assigned RM</span><span className="text-sm text-foreground-950 mt-0.5 block">{lead.assignedRM}</span></div>
                  <div><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Days in Stage</span><span className="text-sm text-foreground-950 mt-0.5 block">{lead.daysInStage} days</span></div>
                  <div className="col-span-2"><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Property Interest</span><span className="text-sm text-foreground-950 mt-0.5 block">{lead.propertyInterest}</span></div>
                  <div><span className="text-[10px] text-foreground-500 uppercase tracking-wider block">Estimated Value</span><span className="text-sm font-bold text-foreground-950 mt-0.5 block">{formatINR(lead.value || 0)}</span></div>
                </div>
                {lead.notes && (
                  <div className="mt-4 pt-4 border-t border-background-200/60">
                    <span className="text-[10px] text-foreground-500 uppercase tracking-wider block mb-1">Notes</span>
                    <p className="text-sm text-foreground-700">{lead.notes}</p>
                  </div>
                )}
              </div>

              {/* Recent Activity */}
              <div className="rounded-xl border border-background-200/60 bg-white p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Recent Activity</h3>
                  <button onClick={() => setActiveTab('activity')} className="text-[10px] text-accent-700 hover:text-accent-400 cursor-pointer whitespace-nowrap">
                    View all <i className="ri-arrow-right-line text-[9px]" />
                  </button>
                </div>
                <div className="space-y-2">
                  {activities.slice(0, 5).map(act => (
                    <div key={act.id} className="flex items-start gap-3 px-3 py-2.5 rounded-lg bg-background-50">
                      <span className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${activityColors[act.type]}`}>
                        <i className={`${activityIcons[act.type]} text-xs`} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground-950">{act.title}</p>
                        <p className="text-[11px] text-foreground-500 truncate">{act.description}</p>
                        <p className="text-[10px] text-foreground-500 mt-0.5">{formatRelativeTime(act.timestamp)} · {act.by}</p>
                      </div>
                    </div>
                  ))}
                  {activities.length === 0 && (
                    <p className="text-xs text-foreground-500 text-center py-6">No activity recorded yet.</p>
                  )}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              {/* Quick Stats */}
              <div className="rounded-xl border border-background-200/60 bg-white p-6 space-y-3">
                <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Quick Stats</h3>
                <div className="space-y-3">
                  {[
                    { label: 'Score', value: lead.score.toString(), sub: '0-100', icon: 'ri-bar-chart-line', color: 'accent' },
                    { label: 'Activities', value: activities.length.toString(), sub: 'total recorded', icon: 'ri-history-line', color: 'teal' },
                    { label: 'Documents', value: documents.length.toString(), sub: 'uploaded', icon: 'ri-folder-line', color: 'primary' },
                    { label: 'Communications', value: communications.length.toString(), sub: 'exchanges', icon: 'ri-chat-3-line', color: 'secondary' },
                  ].map(s => (
                    <div key={s.label} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                          s.color === 'accent' ? 'bg-accent-100 text-accent-700' :
                          s.color === 'teal' ? 'bg-teal-500/15 text-teal-400' :
                          s.color === 'primary' ? 'bg-primary-100 text-primary-700' :
                          'bg-secondary-100 text-secondary-700'
                        }`}><i className={`${s.icon} text-xs`} /></span>
                        <span className="text-xs text-foreground-500">{s.label}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-bold text-foreground-950">{s.value}</span>
                        <span className="text-[10px] text-foreground-500 block">{s.sub}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pipeline Snapshot */}
              {pipelineHistory.length > 0 && (
                <div className="rounded-xl border border-background-200/60 bg-white p-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Pipeline</h3>
                    <button onClick={() => setActiveTab('pipeline')} className="text-[10px] text-accent-700 hover:text-accent-400 cursor-pointer whitespace-nowrap">Full view</button>
                  </div>
                  <div className="space-y-1.5">
                    {pipelineHistory.map((stage, i) => {
                      const isActive = stage.exitedAt === null;
                      const isPast = !isActive;
                      return (
                        <div key={i} className="flex items-center gap-2">
                          <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${isActive ? 'bg-accent-500 ring-2 ring-accent-500/30' : isPast ? 'bg-teal-500' : 'bg-background-200/50'}`} />
                          <span className={`text-xs ${isActive ? 'text-foreground-950 font-semibold' : 'text-foreground-500'}`}>{stage.stage}</span>
                          <span className="text-[10px] text-foreground-500 ml-auto">{stage.durationDays}d</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Add Note */}
              <div className="rounded-xl border border-background-200/60 bg-white p-6">
                <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Quick Note</h3>
                <textarea
                  rows={3}
                  placeholder="Add a quick note about this lead..."
                  value={newNote}
                  onChange={e => setNewNote(e.target.value)}
                  className="w-full px-4 py-2.5 rounded-xl bg-white border border-background-200/60 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-accent-500/50 resize-none"
                />
                <button onClick={handleAddNote} className="mt-2 w-full py-2 rounded-xl bg-accent-500 text-white text-xs font-semibold hover:bg-accent-400 cursor-pointer whitespace-nowrap">
                  <i className="ri-add-line mr-1" /> Add Note
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── ACTIVITY TAB ── */}
        {activeTab === 'activity' && (
          <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
            <div className="px-5 py-3 border-b border-background-200/60 flex items-center justify-between bg-background-50">
              <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Full Activity Timeline</h3>
              <div className="flex items-center gap-2">
                <select
                  value={activityFilter}
                  onChange={e => setActivityFilter(e.target.value)}
                  className="px-3 py-1.5 rounded-lg bg-white border border-background-200/60 text-xs text-foreground-950 cursor-pointer"
                >
                  <option value="">All Types</option>
                  {activityTypes.map(t => (
                    <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="p-6">
              {filteredActivities.length === 0 ? (
                <div className="text-center py-10">
                  <i className="ri-history-line text-2xl text-foreground-500 mb-2 block" />
                  <p className="text-sm text-foreground-500">No activities match your filter.</p>
                </div>
              ) : (
                <div className="relative">
                  {/* Vertical line */}
                  <div className="absolute left-[15px] top-0 bottom-0 w-px bg-background-200/60" />
                  <div className="space-y-4">
                    {filteredActivities.map((act, i) => (
                      <div key={act.id} className="flex items-start gap-4 relative">
                        <span className={`relative z-10 w-[31px] h-[31px] rounded-full flex items-center justify-center flex-shrink-0 border-2 border-white ${activityColors[act.type]}`}>
                          <i className={`${activityIcons[act.type]} text-xs`} />
                        </span>
                        <div className="flex-1 pb-3">
                          <div className="flex items-center gap-2 mb-0.5">
                            <p className="text-sm font-medium text-foreground-950">{act.title}</p>
                            <span className="text-[10px] text-foreground-500">{formatRelativeTime(act.timestamp)}</span>
                          </div>
                          <p className="text-xs text-foreground-700">{act.description}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-[10px] text-foreground-500">{act.by}</span>
                            {act.metadata?.duration && (
                              <>
                                <span className="text-foreground-400 text-[10px]">·</span>
                                <span className="text-[10px] text-foreground-500"><i className="ri-time-line text-[9px] mr-0.5" />{act.metadata.duration}</span>
                              </>
                            )}
                            {act.metadata?.outcome && (
                              <>
                                <span className="text-foreground-400 text-[10px]">·</span>
                                <span className={`text-[10px] font-medium ${act.metadata.outcome.includes('Positive') || act.metadata.outcome.includes('Agreed') || act.metadata.outcome.includes('Confirmed') ? 'text-teal-400' : act.metadata.outcome.includes('Counter') ? 'text-accent-700' : 'text-foreground-500'}`}>{act.metadata.outcome}</span>
                              </>
                            )}
                            {act.metadata?.amount && (
                              <>
                                <span className="text-foreground-400 text-[10px]">·</span>
                                <span className="text-[10px] font-semibold text-green-400">{act.metadata.amount}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── PIPELINE TAB ── */}
        {activeTab === 'pipeline' && (
          <div className="space-y-4">
            {/* Pipeline Visualization */}
            <div className="rounded-xl border border-background-200/60 bg-white p-6">
              <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-5">Pipeline Progression</h3>
              <div className="flex items-start gap-0 overflow-x-auto pb-2">
                {pipelineStages.map((stage, i) => {
                  const historyEntry = pipelineHistory.find(h => h.stage === stage);
                  const isCurrent = i === currentStageIndex;
                  const isPassed = i < currentStageIndex;
                  const isFuture = i > currentStageIndex;
                  return (
                    <div key={stage} className="flex-1 min-w-[120px] relative">
                      {/* Connector */}
                      {i < pipelineStages.length - 1 && (
                        <div className={`absolute top-[18px] left-[50%] w-full h-0.5 ${isPassed ? 'bg-teal-500' : 'bg-background-200/50'}`} />
                      )}
                      <div className="flex flex-col items-center text-center">
                        <span className={`relative z-10 w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold mb-2 ${
                          isPassed ? 'bg-teal-500 text-white' :
                          isCurrent ? 'bg-accent-500 text-white ring-4 ring-accent-500/20' :
                          'bg-background-200/50 text-foreground-500'
                        }`}>
                          {isPassed ? <i className="ri-check-line" /> : isCurrent ? <i className="ri-play-fill text-[10px]" /> : (i + 1)}
                        </span>
                        <span className={`text-[11px] font-semibold ${isCurrent ? 'text-foreground-950' : 'text-foreground-500'}`}>{stage}</span>
                        {historyEntry && (
                          <span className="text-[10px] text-foreground-500 mt-0.5">{historyEntry.durationDays}d</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Stage Details */}
            <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
              <div className="px-5 py-3 border-b border-background-200/60 bg-background-50">
                <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Stage History</h3>
              </div>
              <div className="divide-y divide-background-200/50">
                {pipelineHistory.length === 0 ? (
                  <div className="p-6 text-center text-sm text-foreground-500">No pipeline history recorded.</div>
                ) : (
                  pipelineHistory.map((stage, i) => (
                    <div key={i} className={`px-5 py-4 flex items-center gap-4 ${stage.exitedAt === null ? 'bg-accent-50/50' : ''}`}>
                      <span className={`w-3 h-3 rounded-full flex-shrink-0 ${stage.exitedAt === null ? 'bg-accent-500 ring-2 ring-accent-500/30' : 'bg-teal-500'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm font-semibold ${stage.exitedAt === null ? 'text-foreground-950' : 'text-foreground-700'}`}>{stage.stage}</span>
                          {stage.exitedAt === null && (
                            <span className="text-[9px] font-semibold text-accent-700 bg-accent-100 px-1.5 py-0.5 rounded-full">Current</span>
                          )}
                        </div>
                        <p className="text-[11px] text-foreground-500 mt-0.5">{stage.notes}</p>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xs text-foreground-500">{formatDateTime(stage.enteredAt)}</p>
                        {stage.exitedAt ? (
                          <p className="text-[10px] text-foreground-500">to {formatDateTime(stage.exitedAt)}</p>
                        ) : (
                          <p className="text-[10px] text-accent-700 font-medium">Active</p>
                        )}
                        <p className="text-[10px] font-semibold text-foreground-950 mt-0.5">{stage.durationDays} days</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── DOCUMENTS TAB ── */}
        {activeTab === 'documents' && (
          <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
            <div className="px-5 py-3 border-b border-background-200/60 flex items-center justify-between bg-background-50">
              <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Document Repository</h3>
              <button className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-accent-500 text-white text-xs font-semibold hover:bg-accent-400 cursor-pointer whitespace-nowrap">
                <i className="ri-upload-line text-[10px]" /> Upload
              </button>
            </div>
            {documents.length === 0 ? (
              <div className="p-10 text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-3">
                  <i className="ri-folder-open-line text-xl text-foreground-500" />
                </div>
                <p className="text-sm text-foreground-500">No documents uploaded yet.</p>
                <p className="text-[11px] text-foreground-500 mt-1">Upload KYC, agreements, proposals, or any related files.</p>
              </div>
            ) : (
              <div className="divide-y divide-background-200/50">
                {documents.map(doc => (
                  <div key={doc.id} className="px-5 py-4 flex items-center gap-4 hover:bg-background-50 transition-colors">
                    <span className="w-9 h-9 rounded-lg bg-background-100 flex items-center justify-center flex-shrink-0">
                      <i className={`${docTypeIcons[doc.type]} text-foreground-500`} />
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground-950 truncate">{doc.name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-background-100 text-foreground-500">{docTypeLabels[doc.type]}</span>
                        <span className="text-[10px] text-foreground-500">{doc.size}</span>
                        <span className="text-foreground-400 text-[10px]">·</span>
                        <span className="text-[10px] text-foreground-500">Uploaded {formatDate(doc.uploadedAt)} by {doc.uploadedBy}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 hover:text-foreground-950 cursor-pointer" title="Download">
                        <i className="ri-download-line" />
                      </button>
                      <button className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-rose-500/10 text-foreground-500 hover:text-rose-400 cursor-pointer" title="Delete">
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── COMMUNICATIONS TAB ── */}
        {activeTab === 'communications' && (
          <div className="rounded-xl border border-background-200/60 bg-white overflow-hidden">
            <div className="px-5 py-3 border-b border-background-200/60 flex items-center justify-between bg-background-50">
              <h3 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider">Communication Log</h3>
              <button className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-accent-500 text-white text-xs font-semibold hover:bg-accent-400 cursor-pointer whitespace-nowrap">
                <i className="ri-phone-line text-[10px]" /> Log Call
              </button>
            </div>
            {communications.length === 0 ? (
              <div className="p-10 text-center">
                <div className="w-12 h-12 mx-auto rounded-full bg-background-100 flex items-center justify-center mb-3">
                  <i className="ri-chat-off-line text-xl text-foreground-500" />
                </div>
                <p className="text-sm text-foreground-500">No communications logged yet.</p>
              </div>
            ) : (
              <div className="divide-y divide-background-200/50">
                {communications.map(comm => (
                  <div key={comm.id} className="px-5 py-4 hover:bg-background-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                        comm.direction === 'inbound' ? 'bg-green-500/15 text-green-400' : 'bg-accent-100 text-accent-700'
                      }`}>
                        <i className={`${channelIcons[comm.channel]} text-sm`} />
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-semibold text-foreground-950">{comm.subject}</span>
                          <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full ${comm.direction === 'inbound' ? 'bg-green-500/15 text-green-400' : 'bg-accent-100 text-accent-700'}`}>
                            {comm.direction === 'inbound' ? 'Inbound' : 'Outbound'}
                          </span>
                          <span className="text-[10px] text-foreground-500 capitalize">{comm.channel}</span>
                          {comm.duration && <span className="text-[10px] text-foreground-500">{comm.duration}</span>}
                        </div>
                        <p className="text-xs text-foreground-700 mt-1">{comm.summary}</p>
                        {comm.attachments && comm.attachments.length > 0 && (
                          <div className="flex items-center gap-2 mt-2">
                            {comm.attachments.map((att, j) => (
                              <span key={j} className="flex items-center gap-1 text-[10px] text-accent-700 bg-accent-100 px-2 py-0.5 rounded">
                                <i className="ri-attachment-2 text-[9px]" /> {att}
                              </span>
                            ))}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-[10px] text-foreground-500">{formatDateTime(comm.timestamp)} · {comm.by}</span>
                          <span className="text-foreground-400 text-[10px]">·</span>
                          <span className={`text-[10px] font-medium ${comm.outcome.includes('Positive') || comm.outcome.includes('Confirmed') || comm.outcome.includes('Delivered') || comm.outcome.includes('Signed') ? 'text-teal-400' : comm.outcome.includes('Awaiting') || comm.outcome.includes('Forwarded') || comm.outcome.includes('Query') ? 'text-accent-700' : 'text-foreground-500'}`}>
                            {comm.outcome}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}